import { Program } from '@playbook-technologies/common-enums';
import {
  GetCollectionWorkoutsQuery,
  GetProgramByIdQuery,
  PurchasePageConfigQuery,
  useGetCollectionWorkoutsQuery,
  useGetProgramByIdQuery,
  usePurchasePageConfigQuery,
} from '../../../../generated/graphql';
import { CollectionProgram } from '../../../components/collections/collections.types';
import { programUrl } from '../../helpers/landingPages';
import { Async } from '../../helpers/types';
import { useMappedAsyncResult } from '../../helpers/gql';
import { LiteProgram, WorkoutTiny } from './domain';
import { filterNulls } from '../../helpers/general';
import { toCategoryType } from './mappers';

export const formatPrice = (value: number | null) =>
  value !== null
    ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(value / 100)
    : '';

export const toThumbnailUrl = (
  v:
    | { cdn01?: string | null | undefined; image?: string | null | undefined }
    | null
    | undefined,
) => (v?.cdn01 || '') + (v?.image || '');

export const getTags = <
  T extends Pick<CollectionProgram, 'isVisible' | 'isLocked' | 'archivedAt'>,
>(
  collection: T,
): LiteProgram['tags'] => {
  const tags: LiteProgram['tags'] = [
    collection.isVisible
      ? { text: 'live', type: 'ok' }
      : { text: 'not published', type: 'warn' },
  ];

  if (collection.isLocked) tags.push({ text: 'locked', type: 'info' });
  // if (collection.deletedAt) tags.push({ text: 'removed', type: 'warn' });
  if (collection.archivedAt) tags.push({ text: 'archived', type: 'warn' });
  return tags;
};

export const getType = (accessType: string): LiteProgram['programType'] =>
  accessType === Program.AccessType.Exclusive.value ? 'otp' : 'subscription';

type RawWorkout = { id: number; name?: string | null | undefined } | null;

const toWorkoutTiny = (ws: RawWorkout[]) =>
  Array.isArray(ws)
    ? filterNulls(ws).map(
        ({ id, name = '' }) =>
          ({ workoutId: id, name } as unknown as WorkoutTiny),
      )
    : [];

const useProgram = (id: number | null) => {
  const query = useGetCollectionWorkoutsQuery({
    variables: { id: id || 0 },
    skip: id === null,
    fetchPolicy: 'network-only',
  });
  return useMappedAsyncResult<
    GetCollectionWorkoutsQuery,
    {
      customURL: string;
      workouts: WorkoutTiny[];
      accessType?: string;
      migrationBundleId: number | null;
    }
  >(query, (v) => ({
    customURL: v.collection.influencer?.customURL || '',
    workouts: toWorkoutTiny(v.collection.workouts || []),
    accessType: query.data?.collection.accessType,
    migrationBundleId: v.collection.migrationBundleId ?? null,
  }));
};

const useSalesPageConfig = (id: number | null) => {
  const query = usePurchasePageConfigQuery({
    variables: { programId: id || 0 },
    skip: id === null,
  });
  return useMappedAsyncResult<PurchasePageConfigQuery, boolean>(query, (v) =>
    Boolean(v.purchasePageConfig),
  );
};

const useCustomURL = ({ programId }: { programId: number | null }) => {
  const variables = { id: programId || 0 };
  const skip = programId === null;
  const params = { variables, skip, fetchPolicy: 'network-only' as const };

  const query = useGetCollectionWorkoutsQuery(params); // customURL hack - fix it

  return useMappedAsyncResult<
    GetCollectionWorkoutsQuery,
    { customURL: string }
  >(query, (v) => ({ customURL: v.collection.influencer?.customURL || '' }));
};

const usePrice = (id: number | null) => {
  const query = useGetProgramByIdQuery({
    variables: { id: id || 0 },
    skip: id === null,
  });

  const price = useMappedAsyncResult<
    GetProgramByIdQuery,
    { price: number | null; discountedPrice: number | null }
  >(query, (v) => ({
    price: v?.getPricing?.price || null,
    discountedPrice: v?.getPricing?.discountedPrice || null,
  }));
  return price;
};

const toSubtitle = (collection: CollectionProgram) => {
  const { duration } = collection;
  return duration && duration.length >= 2
    ? duration[0] + ' ' + duration[1]
    : '';
};

export const useLiteProgram = (
  collection: CollectionProgram | null,
): Async<LiteProgram> => {
  const programId = collection?.id || null;

  const asyncCustomURL = useCustomURL({ programId });
  const price = usePrice(programId);
  const salesPageConfig = useSalesPageConfig(programId);
  const program = useProgram(programId);

  if (!collection || !programId) return { type: 'not-fetched' };
  if (price.type !== 'fetched') return price;
  if (asyncCustomURL.type !== 'fetched') return asyncCustomURL;
  if (salesPageConfig.type !== 'fetched') return salesPageConfig;
  if (program.type !== 'fetched') return program;

  return {
    type: 'fetched',
    value: {
      programId,
      programType: getType(collection.accessType),
      programState: collection.isVisible ? 'published' : 'not-publish-ready',
      tags: getTags(collection),
      categoryType: toCategoryType(collection.type),

      title: collection.name,
      subtitle: toSubtitle(collection),

      thumbnailUrl: toThumbnailUrl(collection.mediaCover),
      hasSalesPageConfig: salesPageConfig.value,
      workouts: program.value.workouts,
      salesPageUrl: programUrl(program.value, collection),
      price: formatPrice(price.value.price),
      discountedPrice:
        price.value.discountedPrice === null
          ? null
          : formatPrice(price.value.discountedPrice),
      accessType: program.value.accessType || '',
      migrationBundleId: program.value.migrationBundleId,
    },
  };
};
